import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermitBenchmarkingURLState } from '@/hooks/usePermitBenchmarkingURLState';
import { updateURLState } from '@/utils/urlParams';
import { Place } from '@/contexts/PlaceNamesContext';
import { HeroSection } from './components/HeroSection';
import { StepSection } from './components/StepSection';
import { TabsSection } from './components/TabsSection';
import { ChartsSection } from './components/ChartsSection';
import { JurisdictionSnapshotSection } from './components/JurisdictionSnapshotSection';
import { OverTimeView } from './components/OverTimeView';
import { usePeerCities } from '@/hooks/usePeerCities';
import { useConsolidatedMetrics } from '@/hooks/useConsolidatedMetrics';
import { formatPlaceName } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { ProcessedMetrics } from '@/types';
import { supabase } from '@/lib/supabase';
import { calcLatestValueAndGrowth } from '@/utils/metrics';
import { useSavedBenchmarkComparison } from '@/hooks/useSavedBenchmarkComparison';
import { usePageDownloadHandler } from '@/hooks/usePageDownloadHandler';
import { Loader2 } from 'lucide-react';
import { TAB_IDS, TabId } from './types/data';
import SEOHead from '@/components/shared/SEOHead';
import { SEO_CONFIGS } from '@/hooks/useSEO';
import { handleHashScroll } from '@/utils/shareUtils';

export const NewBenchmarksUI: React.FC = () => {
  // ----- URL state ----- //
  const [, setSearchParams] = useSearchParams();
  const { selectedPlace: urlPlace, peers: urlPeers, activeTab: urlTab } = usePermitBenchmarkingURLState();
  const hasUrlState = !!urlPlace || (urlPeers && urlPeers.length > 0);

  const [selectedPlace, setSelectedPlace] = useState<Place | null>(urlPlace || null);
  const [displayName, setDisplayName] = useState<string>('');
  const [peerMetrics, setPeerMetrics] = useState<{ peer: Place; data: ProcessedMetrics[] }[]>([]);
  const [loadingPeerMetrics, setLoadingPeerMetrics] = useState(false);
  const [peerMetricsError, setPeerMetricsError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(urlTab || TAB_IDS.PEER_COMPARISON);
  const [hasUserCleared, setHasUserCleared] = useState(false);

  // Track first render for URL syncing
  const isInitialRender = useRef(true);

  // Load saved comparison (may be overridden by URL params)
  const { data: savedComparison, loading: loadingSaved, error: savedError } = useSavedBenchmarkComparison();

  // Handle page-level download requests from URL params
  usePageDownloadHandler();

  // ---------------- Data precedence ---------------- //
  // 1) URL params (shared link) win over saved comparison.
  // 2) If no URL params, fall back to saved comparison.

  // Get peer cities, initialise with either URL peers or saved peers (later decided)
  const {
    peerCities,
    loadingPeers,
    peersError,
    addPeer,
    removePeer,
    replacePeers,
  } = usePeerCities({
    selectedPlace,
    initialPeers: hasUrlState ? urlPeers : (savedComparison?.peerPlaces ?? []),
  });

  // Handle peer removal - URL sync handled automatically by useEffect
  const handleRemovePeer = useCallback((placeFips: string) => {
    removePeer(placeFips);
  }, [removePeer]);

  // Apply saved comparison (only if no URL override and user hasn't explicitly cleared)
  useEffect(() => {
    if (!savedComparison || hasUrlState || hasUserCleared) return;

    // Set place if not already set
    if (!selectedPlace && savedComparison.selectedPlace) {
      setSelectedPlace(savedComparison.selectedPlace);
    }

    // Replace peers with saved peers
    if (savedComparison.peerPlaces?.length) {
      replacePeers(savedComparison.peerPlaces);
    }

    // Active tab from saved comparison (only if no URL tab)
    if (!urlTab) {
      setActiveTab((savedComparison.activeTab as TabId) || TAB_IDS.PEER_COMPARISON);
    }
  }, [savedComparison, hasUrlState, selectedPlace, replacePeers, urlTab, hasUserCleared]);

  // Reset hasUserCleared flag when URL state is present (URL params take precedence)
  useEffect(() => {
    if (hasUrlState) {
      setHasUserCleared(false);
    }
  }, [hasUrlState]);

  // ---------------- URL sync ---------------- //
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return; // Do not overwrite the URL we just parsed
    }
    updateURLState(setSearchParams, selectedPlace, peerCities, activeTab);
  }, [selectedPlace, peerCities, activeTab, setSearchParams]);

  // Get the selected place metrics
  const { data: selectedPlaceMetrics, loading: metricsLoading, error: metricsError } = useConsolidatedMetrics(
    selectedPlace?.name || null,
    selectedPlace?.place_fips || null
  );

  const fetchPeerMetrics = useCallback(async () => {
    if (!peerCities.length) {
      setPeerMetrics([]);
      setLoadingPeerMetrics(false);
      setPeerMetricsError(null);
      return;
    }

    setLoadingPeerMetrics(true);
    setPeerMetricsError(null);

    try {
      const peerResults = await Promise.all(
        peerCities.map(async (peer) => {
          const { data: supabaseData, error: supabaseError } = await supabase
            .from('consolidated_jurisdiction_metrics')
            .select('*')
            .eq('Place', peer.name)
            .eq('place_fips', peer.place_fips)
            .order('year', { ascending: false })
            .limit(6);  // Get 6 years of data for proper growth calculations

          if (supabaseError) {
            throw new Error(`Error fetching data for ${peer.name}: ${supabaseError.message}`);
          }

          if (!supabaseData || supabaseData.length === 0) {
            return { peer, data: [] };
          }

          const processedData = supabaseData.map(record => ({
            ...record,
            population_metrics: calcLatestValueAndGrowth(supabaseData, 'total_population'),
            housing_units_metrics: calcLatestValueAndGrowth(supabaseData, 'housing_units_per_1000_residents'),
            price_to_income_metrics: calcLatestValueAndGrowth(supabaseData, 'price_to_income'),
            permits_metrics: calcLatestValueAndGrowth(supabaseData, 'bps_total_units'),
          }));

          return { peer, data: processedData };
        })
      );

      setPeerMetrics(peerResults);
      setLoadingPeerMetrics(false);
    } catch (error) {
      console.error('Error fetching peer metrics:', error);
      setPeerMetricsError(error instanceof Error ? error.message : 'Failed to fetch peer metrics');
      setPeerMetrics([]);
      setLoadingPeerMetrics(false);
    }
  }, [peerCities]);

  // Handler for hero search selection
  const handlePlaceSelect = (place: Place) => {
    console.log('[NewBenchmarksUI] Received place from HeroSection:', place);
    console.log('[NewBenchmarksUI] Place details:', {
      name: place.name,
      place_fips: place.place_fips,
      fipsType: typeof place.place_fips
    });
    setSelectedPlace(place);
    setHasUserCleared(false); // Reset flag when user selects a new place
    // Let the usePeerCities hook handle peer management when selectedPlace changes
  };

  // Handler for clearing selected place
  const handleClearPlace = () => {
    console.log('[NewBenchmarksUI] Clearing selected place');
    setSelectedPlace(null);
    setDisplayName('');
    // Clear peers when clearing the selected place
    replacePeers([]);
    setHasUserCleared(true); // Set flag to indicate user explicitly cleared
  };

  // Effect to fetch peer metrics when peers change
  useEffect(() => {
    fetchPeerMetrics();
  }, [fetchPeerMetrics]);

  // Effect to update display name when selected place changes
  useEffect(() => {
    if (selectedPlace) {
      setDisplayName(formatPlaceName(selectedPlace.name));
    }
  }, [selectedPlace]);

  // Handle hash-based scrolling for shared chart links
  useEffect(() => {
    // Only handle hash scrolling if we have a selected place and the content has loaded
    if (selectedPlace && !metricsLoading && !loadingPeers && !loadingPeerMetrics) {
      console.log(`🎯 Hash scroll effect triggered - Content loaded, checking for hash...`);
      // Small delay to ensure all charts are rendered
      const timer = setTimeout(() => {
        handleHashScroll();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [selectedPlace, metricsLoading, loadingPeers, loadingPeerMetrics]);

  // Handle hash changes when navigating within the page
  useEffect(() => {
    const handleHashChange = () => {
      if (selectedPlace && !metricsLoading && !loadingPeers && !loadingPeerMetrics) {
        handleHashScroll();
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [selectedPlace, metricsLoading, loadingPeers, loadingPeerMetrics]);

  // Prepare metrics data for saving
  const metricsData = {
    selectedPlaceMetrics,
    peerMetrics,
    loading: metricsLoading || loadingPeers || loadingPeerMetrics,
    error: metricsError || peersError || peerMetricsError,
  };

  // Show loading state while fetching saved comparison
  if (loadingSaved) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
  };

  // Dynamic SEO configuration based on selected place
  const seoConfig = {
    ...SEO_CONFIGS.benchmarking,
    ...(selectedPlace && {
      title: `${displayName || formatPlaceName(selectedPlace.name)} Permit Benchmarking - Compare Performance | PermitLab`,
      description: `Benchmark ${displayName || formatPlaceName(selectedPlace.name)}'s permitting performance against peer jurisdictions. Analyze permits per capita, processing times, and housing growth metrics.`,
      ogTitle: `${displayName || formatPlaceName(selectedPlace.name)} Permit Benchmarking - Compare Performance | PermitLab`,
      ogDescription: `Benchmark ${displayName || formatPlaceName(selectedPlace.name)}'s permitting performance against peer jurisdictions. Analyze permits per capita, processing times, and housing growth metrics.`,
      canonical: `https://permitlab.govstream.ai/benchmarking?place=${encodeURIComponent(selectedPlace.place_fips)}`,
      ogUrl: `https://permitlab.govstream.ai/benchmarking?place=${encodeURIComponent(selectedPlace.place_fips)}`,
    })
  };

  return (
    <div className="flex flex-col">
      <SEOHead {...seoConfig} />
      <div className="flex-none">
        <HeroSection 
          selectedPlace={selectedPlace}
          onPlaceSelect={handlePlaceSelect}
          onClear={handleClearPlace}
          displayName={displayName}
        />
      </div>
      
      {selectedPlace && (
        <div className="flex-1">
          <TabsSection 
            selectedPlace={selectedPlace} 
            peerPlaces={peerCities}
            metricsData={metricsData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {activeTab === TAB_IDS.PEER_COMPARISON && (
            <>
              <StepSection 
                selectedPlace={selectedPlace}
                displayName={displayName}
                peers={peerCities}
                loadingPeers={loadingPeers}
                peersError={peersError}
                onAddPeer={addPeer}
                onRemovePeer={handleRemovePeer}
              />

              <ChartsSection
                selectedPlace={selectedPlace}
                selectedPlaceMetrics={selectedPlaceMetrics}
                peerMetrics={peerMetrics}
                loading={metricsLoading || loadingPeers || loadingPeerMetrics}
                error={metricsError || peersError || peerMetricsError}
              />
            </>
          )}

          {activeTab === TAB_IDS.JURISDICTION_SNAPSHOT && (
            <JurisdictionSnapshotSection
              selectedPlace={selectedPlace}
              displayName={displayName}
              peerPlaces={peerCities}
              metricsData={metricsData}
            />
          )}

          {activeTab === TAB_IDS.JURISDICTION_OVERTIME && (
            <OverTimeView selectedPlace={selectedPlace} />
          )}
        </div>
      )}
    </div>
  );
};